import { Link } from '@tanstack/react-router'
import {
  BarChart3,
  Bot,
  Cable,
  Headset,
  Inbox,
  MessageCircleQuestion,
  Radio,
  Sparkles,
  UserRoundCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  StatCard,
} from '@/components/ui'
import { useSession } from '@/features/auth/session'
import { CreditsDashboardCard } from '@/features/billing/components/CreditsDashboardCard'
import { useAnalyticsOverview } from '@/features/analytics/queries'
import { useTenantMembers } from '@/features/tenants/queries'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { formatDate, translateEnumValue } from '@/lib/formatting'

/** Janela do resumo de atividade do dashboard (dias). */
const ACTIVITY_WINDOW_DAYS = 30

function DataRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-foreground-muted">{label}</span>
      <span className="font-medium text-foreground-primary">{value}</span>
    </div>
  )
}

/** Dashboard inicial: dados reais do tenant, resumo de atividade e equipe — sem nenhum modulo fingido. */
export function DashboardPage() {
  const { t, i18n } = useTranslation()
  const { user } = useSession()
  const { tenant } = useCurrentTenant()
  const membersQuery = useTenantMembers(tenant?.id)
  const activityQuery = useAnalyticsOverview(tenant?.id, ACTIVITY_WINDOW_DAYS)

  const moduleSummaries = [
    {
      key: 'desk',
      icon: Headset,
      to: '/app/$tenantSlug/desk' as const,
      title: t('shell.nav.desk'),
      description: t('dashboard.shortcuts.desk'),
    },
    {
      key: 'agents',
      icon: Bot,
      to: '/app/$tenantSlug/agents' as const,
      title: t('shell.nav.agents'),
      description: t('dashboard.shortcuts.agents'),
    },
    {
      key: 'knowledge',
      icon: MessageCircleQuestion,
      to: '/app/$tenantSlug/knowledge' as const,
      title: t('shell.nav.knowledge'),
      description: t('dashboard.shortcuts.knowledge'),
    },
    {
      key: 'channels',
      icon: Radio,
      to: '/app/$tenantSlug/channels' as const,
      title: t('shell.nav.channels'),
      description: t('dashboard.shortcuts.channels'),
    },
    {
      key: 'integrations',
      icon: Cable,
      to: '/app/$tenantSlug/integrations' as const,
      title: t('shell.nav.integrations'),
      description: t('dashboard.shortcuts.integrations'),
    },
    {
      key: 'analytics',
      icon: BarChart3,
      to: '/app/$tenantSlug/analytics' as const,
      title: t('shell.nav.analytics'),
      description: t('dashboard.shortcuts.analytics'),
    },
  ]

  if (user === null || tenant === null) {
    return null
  }

  const tenantStatusLabels: Readonly<Record<string, string>> = {
    Provisioning: t('dashboard.organization.statuses.Provisioning'),
    Active: t('dashboard.organization.statuses.Active'),
    Suspended: t('dashboard.organization.statuses.Suspended'),
    Disabled: t('dashboard.organization.statuses.Disabled'),
    Deleted: t('dashboard.organization.statuses.Deleted'),
  }

  const formatNumber = (value: number) => new Intl.NumberFormat(i18n.language).format(value)
  const activity = activityQuery.data
  const members = membersQuery.data

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-foreground-primary">
        {t('dashboard.greeting', { name: user.displayName })}
      </h1>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-foreground-primary">
          {t('dashboard.activity.title', { days: ACTIVITY_WINDOW_DAYS })}
        </h2>
        {activityQuery.isError ? (
          <Alert variant="danger" title={t('dashboard.activity.loadError')}>
            {t('retry')}
          </Alert>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {activity === undefined ? (
              Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-[92px] w-full" />
              ))
            ) : (
              <>
                <StatCard
                  icon={Inbox}
                  label={t('dashboard.activity.inbound')}
                  value={formatNumber(activity.inbound.total)}
                />
                <StatCard
                  icon={Bot}
                  label={t('dashboard.activity.executions')}
                  value={formatNumber(activity.agentExecutions.total)}
                />
                <StatCard
                  icon={Sparkles}
                  label={t('dashboard.activity.autoReplies')}
                  value={formatNumber(activity.agentExecutions.autoReplies)}
                />
                <StatCard
                  icon={UserRoundCheck}
                  label={t('dashboard.activity.suggestions')}
                  value={formatNumber(activity.agentExecutions.suggestionsToHuman)}
                />
              </>
            )}
          </div>
        )}
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.organization.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <DataRow label={t('dashboard.organization.nameLabel')} value={tenant.name} />
            <DataRow label={t('dashboard.organization.slugLabel')} value={tenant.slug} />
            <div className="flex items-center justify-between gap-4">
              <span className="text-foreground-muted">
                {t('dashboard.organization.statusLabel')}
              </span>
              <Badge variant={tenant.status === 'Active' ? 'success' : 'neutral'}>
                {translateEnumValue(tenantStatusLabels, tenant.status)}
              </Badge>
            </div>
            <DataRow
              label={t('dashboard.organization.createdAtLabel')}
              value={formatDate(tenant.createdAt, i18n.language)}
            />
          </CardContent>
        </Card>

        <CreditsDashboardCard tenantId={tenant.id} tenantSlug={tenant.slug} />

        {/* `data === null` = sem permissão para ver a equipe → card oculto (como no original). */}
        {members !== null && (
          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.members.title')}</CardTitle>
              {members !== undefined ? (
                <CardDescription>
                  {members.length === 1
                    ? t('dashboard.members.countSingular', { count: members.length })
                    : t('dashboard.members.countPlural', { count: members.length })}
                </CardDescription>
              ) : null}
            </CardHeader>
            <CardContent>
              {membersQuery.isError ? (
                <Alert variant="danger" title={t('dashboard.members.loadError')} />
              ) : members === undefined ? (
                <Skeleton className="h-5 w-24" />
              ) : (
                <Link
                  to="/app/$tenantSlug/team"
                  params={{ tenantSlug: tenant.slug }}
                  className="text-sm font-medium text-action-primary hover:underline"
                >
                  {t('dashboard.members.viewAll')}
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div>
        <h2 className="text-base font-semibold text-foreground-primary">
          {t('dashboard.modules.title')}
        </h2>
        <p className="text-sm text-foreground-muted">{t('dashboard.modules.subtitle')}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {moduleSummaries.map((module) => (
            <Link
              key={module.key}
              to={module.to}
              params={{ tenantSlug: tenant.slug }}
              className="flex items-start gap-3 rounded-lg border border-border-default bg-background-elevated p-4 text-left hover:border-border-strong"
            >
              <module.icon
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0 text-foreground-muted"
              />
              <div>
                <p className="text-sm font-medium text-foreground-primary">{module.title}</p>
                <p className="text-xs text-foreground-muted">{module.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
